import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook, ShieldCheck, Landmark, BadgeCheck } from "lucide-react";
import { COMPANY, SERVICES, SUBURBS } from "../data/site";

const GOLD = "#C5A059";

function CredentialBadge({ Icon, label, value, testid }) {
  return (
    <div
      className="flex items-center gap-2.5 rounded-md border border-white/10 bg-white/[0.03] px-3 py-2"
      data-testid={testid}
    >
      <span
        className="flex h-7 w-7 items-center justify-center rounded-sm"
        style={{ backgroundColor: "rgba(197,160,89,0.10)", border: `1px solid ${GOLD}33` }}
      >
        <Icon size={14} style={{ color: GOLD }} strokeWidth={1.75} />
      </span>
      <div className="leading-tight">
        <div className="text-[10px] uppercase tracking-[0.16em] text-white/55">{label}</div>
        <div className="text-[12px] text-white/90 font-medium tabular-nums">{value}</div>
      </div>
    </div>
  );
}

function Credentials({ className = "" }) {
  return (
    <div className={className} data-testid="footer-credentials">
      <div className="overline mb-3" style={{ color: "rgba(255,255,255,0.55)" }}>
        Licensed &amp; Insured
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
        <CredentialBadge
          Icon={ShieldCheck}
          label="QBCC Licence"
          value="15553095"
          testid="footer-qbcc"
        />
        <CredentialBadge
          Icon={Landmark}
          label="ABN"
          value="14 516 161 647"
          testid="footer-abn"
        />
        <CredentialBadge
          Icon={BadgeCheck}
          label="NRMA Insured"
          value="up to $25M"
          testid="footer-nrma"
        />
      </div>
    </div>
  );
}

export default function Footer() {
  return (
    <footer className="bg-[#0B1426] text-white" data-testid="site-footer">
      {/* MOBILE — compact footer */}
      <div className="lg:hidden container-cps pt-14 pb-10">
        <div className="font-display text-2xl tracking-tight">
          Connolly's<span style={{ color: "#C5A059" }}>.</span>
          <span className="ml-2 text-xs uppercase tracking-[0.18em] text-white/55 font-normal">Painting Solutions</span>
        </div>

        <div className="hairline-gold mt-6" />

        <div className="mt-6 space-y-3 text-sm">
          <a href={`tel:${COMPANY.phoneTel}`} className="flex items-center gap-3 text-white/85" data-testid="footer-phone-mobile">
            <Phone size={14} /> {COMPANY.phone}
          </a>
          <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-3 text-white/85" data-testid="footer-email-mobile">
            <Mail size={14} /> {COMPANY.email}
          </a>
          <div className="flex items-center gap-3 text-white/85">
            <MapPin size={14} /> Sunshine Coast, QLD
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2 text-sm text-white/75">
          <Link to="/gallery" className="hover:text-white">Gallery</Link>
          <Link to="/reviews" className="hover:text-white">Reviews</Link>
          <Link to="/suburbs" className="hover:text-white">Service Areas</Link>
          <Link to="/contact" className="hover:text-white">Contact</Link>
          <Link to="/quote" className="hover:text-white">Get a Free Quote</Link>
          <Link to="/privacy-policy" className="hover:text-white" data-testid="footer-privacy-mobile">Privacy Policy</Link>
        </div>

        <Credentials className="mt-8" />

        <div className="mt-8 pt-6 border-t border-white/10 flex items-center justify-between text-[11px] text-white/45">
          <div>© {new Date().getFullYear()} Connolly's Painting</div>
          <div className="flex items-center gap-3">
            <a href={COMPANY.social.instagram} aria-label="Instagram"><Instagram size={14} /></a>
            <a href={COMPANY.social.facebook} aria-label="Facebook"><Facebook size={14} /></a>
            <Link to="/admin/login" className="text-white/30 hover:text-white/60 text-[10px] uppercase tracking-wider">Admin</Link>
          </div>
        </div>
      </div>

      {/* DESKTOP — full footer */}
      <div className="hidden lg:block container-cps pt-32 pb-20">
        <div className="grid grid-cols-12 gap-12">
          <div className="col-span-4">
            <div className="font-display text-3xl tracking-tight">
              Connolly's<span style={{ color: "#C5A059" }}>.</span>
            </div>
            <div className="overline mt-2" style={{ color: "rgba(255,255,255,0.55)" }}>Painting Solutions</div>
            <p className="mt-6 text-white/70 leading-relaxed max-w-md">
              Premium painting for Sunshine Coast homes and businesses. Built on detail, finished on time, finished properly.
            </p>
            <div className="hairline-gold mt-8" />
            <div className="mt-6 space-y-3 text-sm">
              <a href={`tel:${COMPANY.phoneTel}`} className="flex items-center gap-3 text-white/85 hover:text-white" data-testid="footer-phone">
                <Phone size={14} /> {COMPANY.phone}
              </a>
              <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-3 text-white/85 hover:text-white" data-testid="footer-email">
                <Mail size={14} /> {COMPANY.email}
              </a>
              <div className="flex items-center gap-3 text-white/85">
                <MapPin size={14} /> {COMPANY.area}
              </div>
            </div>
          </div>

          <div className="col-span-3">
            <div className="overline mb-5" style={{ color: "rgba(255,255,255,0.7)" }}>Services</div>
            <ul className="space-y-3 text-sm text-white/75">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link to={`/${s.slug}`} className="hover:text-white" data-testid={`footer-service-${s.slug}`}>{s.name}</Link>
                </li>
              ))}
              <li>
                <Link to="/body-corporate-painters-sunshine-coast" className="hover:text-white" data-testid="footer-service-body-corporate">
                  Body Corporate Painting
                </Link>
              </li>
            </ul>
          </div>

          <div className="col-span-3">
            <div className="overline mb-5" style={{ color: "rgba(255,255,255,0.7)" }}>Service Areas</div>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm text-white/75">
              {SUBURBS.map((s) => (
                <li key={s.slug}>
                  <Link to={`/${s.slug}`} className="hover:text-white" data-testid={`footer-suburb-${s.slug}`}>{s.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-2">
            <div className="overline mb-5" style={{ color: "rgba(255,255,255,0.7)" }}>Company</div>
            <ul className="space-y-3 text-sm text-white/75">
              <li><Link to="/gallery" className="hover:text-white" data-testid="footer-gallery">Gallery</Link></li>
              <li><Link to="/reviews" className="hover:text-white" data-testid="footer-reviews">Reviews</Link></li>
              <li><Link to="/quote" className="hover:text-white" data-testid="footer-quote">Get a Free Quote</Link></li>
              <li><Link to="/privacy-policy" className="hover:text-white" data-testid="footer-privacy">Privacy Policy</Link></li>
              <li><Link to="/admin/login" className="hover:text-white text-white/40" data-testid="footer-admin-login">Admin</Link></li>
            </ul>
            <div className="mt-6 flex gap-3">
              <a href={COMPANY.social.instagram} aria-label="Instagram" className="text-white/60 hover:text-white"><Instagram size={18} /></a>
              <a href={COMPANY.social.facebook} aria-label="Facebook" className="text-white/60 hover:text-white"><Facebook size={18} /></a>
            </div>
          </div>
        </div>

        <Credentials className="mt-16" />

        <div className="mt-10 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-white/45">
          <div>© {new Date().getFullYear()} Connolly's Painting Solutions. All rights reserved.</div>
          <div>QBCC Lic. 15553095 · ABN 14 516 161 647 · Sunshine Coast, QLD</div>
        </div>
      </div>
    </footer>
  );
}
